import { Anchor, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { WizardHandlingUnitCard } from './WizardHandlingUnitCard';
import { unitMessages } from '../../../../utils/unit-messages';
import { convertWeight } from '../../../../utils/weight-utils';
import { createEmptyHandlingUnit } from '../../create-empty-handling-unit';

import type { LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { FreightBookingFormState } from '../../freight-booking-wizard-types';
import type { MessageDescriptor } from 'react-intl';

// Short abbreviation message per weight unit, for the summary's total label.
const WEIGHT_UNIT_ABBREVIATION: Record<WeightUnit, MessageDescriptor> = {
  Pounds: unitMessages.pounds,
  Ounces: unitMessages.ounces,
  Kilograms: unitMessages.kilograms,
  Grams: unitMessages.grams,
};

export type HandlingUnitsStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
  displayWeightUnit: WeightUnit;
  productSuggestions?: string[];
  onProductSearch?: (query: string) => void;
  onProductSelect?: (commodityId: string, productName: string) => void;
};

export const HandlingUnitsStep = ({
  formState,
  onFormChange,
  displayWeightUnit,
  productSuggestions,
  onProductSearch,
  onProductSelect,
}: HandlingUnitsStepProps) => {
  const intl = useIntl();

  const handleUnitChange = (index: number, updated: LtlHandlingUnit) => {
    const handlingUnits = [...formState.handlingUnits];
    handlingUnits[index] = updated;
    onFormChange({ ...formState, handlingUnits });
  };

  const handleAddUnit = () =>
    onFormChange({
      ...formState,
      handlingUnits: [...formState.handlingUnits, createEmptyHandlingUnit(formState.handlingUnits, displayWeightUnit)],
    });

  const handleRemoveUnit = (index: number) =>
    onFormChange({ ...formState, handlingUnits: formState.handlingUnits.filter((_, idx) => idx !== index) });

  return (
    <Stack data-testid='handling-units-step' gap='md'>
      {formState.handlingUnits.map((unit, idx) => (
        <WizardHandlingUnitCard
          displayWeightUnit={displayWeightUnit}
          index={idx}
          key={unit.id}
          onChange={(updated) => handleUnitChange(idx, updated)}
          onProductSearch={onProductSearch}
          onProductSelect={onProductSelect}
          onRemove={idx > 0 ? () => handleRemoveUnit(idx) : undefined}
          productSuggestions={productSuggestions}
          unit={unit}
        />
      ))}
      <Anchor component='button' data-testid='add-handling-unit' onClick={handleAddUnit} size='sm' type='button'>
        {intl.formatMessage(handlingUnitsStepMessages.addHandlingUnit)}
      </Anchor>
    </Stack>
  );
};

export type HandlingUnitsStepSummaryProps = {
  formState: FreightBookingFormState;
  displayWeightUnit: WeightUnit;
};

export const HandlingUnitsStepSummary = ({ formState, displayWeightUnit }: HandlingUnitsStepSummaryProps) => {
  const intl = useIntl();

  // Commodity weights are stored in each commodity's own weightUnit (e.g. Ounces), so
  // convert every weight to the seller's preferred display unit before summing.
  const totalWeight = formState.handlingUnits.reduce(
    (unitSum, unit) =>
      unitSum +
      unit.commodities.reduce(
        (commoditySum, commodity) =>
          commoditySum + convertWeight(commodity.weight || 0, commodity.weightUnit ?? displayWeightUnit, displayWeightUnit),
        0,
      ),
    0,
  );
  const totalWeightRounded = Math.round(totalWeight * 100) / 100;

  return (
    <Stack data-testid='handling-units-summary' gap={2}>
      <Text c='var(--adl-text-primary)' fw={600} fz='sm'>
        {intl.formatMessage(handlingUnitsStepMessages.handlingSectionLabel)}
      </Text>
      <Text c='var(--adl-text-primary)' fw={400} fz='sm'>
        {intl.formatMessage(handlingUnitsStepMessages.summary, {
          count: formState.handlingUnits.length,
          weight: totalWeightRounded,
          unit: intl.formatMessage(WEIGHT_UNIT_ABBREVIATION[displayWeightUnit]),
        })}
      </Text>
    </Stack>
  );
};
